// frontend/src/components/handbook/HandbookOverview.js
import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Briefcase,
  Plane,
  CheckCircle2,
  Settings,
  BookOpen,
  CheckSquare,
  Layers,
} from "lucide-react";
import { getChapterProgress } from "../../utils/handbookProgress";
import ContentTypeBadge from "./ContentTypeBadge";
import ChapterStatusBadge, { titleStyleForStatus } from "./ChapterStatusBadge";

const SECTION_ICONS = {
  "vor-event": Briefcase,
  "waehrend-event": Plane,
  "nach-event": CheckCircle2,
  operations: Settings,
};

const SECTION_ACCENT = {
  "vor-event": "from-[#00A3E0]/10 to-[#00A3E0]/0",
  "waehrend-event": "from-[#84BD00]/10 to-[#84BD00]/0",
  "nach-event": "from-[#FC0058]/10 to-[#FC0058]/0",
  operations: "from-[#002855]/10 to-[#002855]/0",
};

const FILTERS = [
  { id: "all", label: "Alle", Icon: Layers },
  { id: "operational", label: "Daily Checks", Icon: CheckSquare },
  { id: "reference", label: "Lesestoff", Icon: BookOpen },
];

const filterChapters = (chapters, mode) => {
  if (mode === "all") return chapters;
  if (mode === "operational") {
    return chapters.filter(
      (c) => c.content_type === "operational" || c.content_type === "mixed"
    );
  }
  if (mode === "reference") {
    return chapters.filter(
      (c) => c.content_type === "reference" || c.content_type === "mixed"
    );
  }
  return chapters;
};

const sectionAggregate = (chapters) => {
  let done = 0;
  let total = 0;
  for (const c of chapters) {
    const { done: d, total: t } = getChapterProgress(c.slug, c.checklists);
    done += d;
    total += t;
  }
  return { done, total };
};

const SectionCard = ({ section, chapters }) => {
  const Icon = SECTION_ICONS[section.id] || BookOpen;
  const accent = SECTION_ACCENT[section.id] || "from-gray-100 to-gray-50";
  const { done, total } = sectionAggregate(chapters);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const firstSlug = chapters[0]?.slug;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:border-[#00A3E0] hover:shadow-md transition-all">
      <Link
        to={firstSlug ? `/coach-handbook/${firstSlug}` : "#"}
        className={`block bg-gradient-to-br ${accent} px-5 py-4 border-b border-gray-100 group`}
      >
        <div className="flex items-center justify-between">
          <Icon className="w-7 h-7 text-[#002855]" />
          <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
            {chapters.length} Kapitel
          </span>
        </div>
        <h2 className="mt-2 text-lg font-semibold text-[#002855] group-hover:text-[#00A3E0] transition-colors flex items-center justify-between gap-2">
          <span className="truncate">{section.title}</span>
          <ArrowRight className="w-4 h-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
        </h2>
        {total > 0 && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>{done} / {total} erledigt</span>
              <span>{pct}%</span>
            </div>
            <div className="h-1 bg-white/60 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#84BD00] transition-all duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}
      </Link>

      {chapters.length > 0 && (
        <ul className="divide-y divide-gray-100">
          {chapters.slice(0, 6).map((chapter) => {
            const { done: cd, total: ct } = getChapterProgress(
              chapter.slug,
              chapter.checklists
            );
            const allDone = ct > 0 && cd === ct;
            return (
              <li key={chapter.slug}>
                <Link
                  to={`/coach-handbook/${chapter.slug}`}
                  className="flex items-center gap-2 px-5 py-2 text-sm hover:bg-gray-50 transition-colors"
                >
                  <ContentTypeBadge
                    type={chapter.content_type}
                    compact
                    className="flex-shrink-0"
                  />
                  <span
                    className={`flex-1 truncate text-gray-700 ${titleStyleForStatus(
                      chapter.status
                    )}`}
                  >
                    {chapter.title}
                  </span>
                  <ChapterStatusBadge
                    status={chapter.status}
                    compact
                    className="flex-shrink-0"
                  />
                  {ct > 0 && (
                    <span
                      className={`text-xs flex-shrink-0 ${
                        allDone ? "text-[#84BD00]" : "text-gray-400"
                      }`}
                    >
                      {allDone ? (
                        <CheckCircle2 className="w-4 h-4" />
                      ) : (
                        `${cd}/${ct}`
                      )}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
          {chapters.length > 6 && firstSlug && (
            <li>
              <Link
                to={`/coach-handbook/${firstSlug}`}
                className="block px-5 py-2 text-xs text-[#00A3E0] hover:bg-gray-50 font-medium"
              >
                + {chapters.length - 6} weitere Kapitel öffnen
              </Link>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

const HandbookOverview = ({ handbook }) => {
  const [mode, setMode] = useState("all");

  const filteredSections = useMemo(() => {
    if (!handbook?.sections) return [];
    return handbook.sections
      .map((section) => ({
        ...section,
        filteredChapters: filterChapters(section.chapters, mode),
      }))
      .filter((s) => s.filteredChapters.length > 0);
  }, [handbook, mode]);

  if (!handbook) return null;

  return (
    <div className="max-w-5xl">
      <header className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-[#002855] mb-2">
          {handbook.title || "Coach Handbook"}
        </h1>
        {handbook.subtitle && (
          <p className="text-base text-gray-600">{handbook.subtitle}</p>
        )}
        <p className="mt-3 text-sm text-gray-500">
          Tipp: Drücke{" "}
          <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded font-mono text-xs">
            ⌘
          </kbd>{" "}
          <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded font-mono text-xs">
            K
          </kbd>{" "}
          um direkt nach einem Stichwort zu suchen.
        </p>
      </header>

      {/* Mode filter */}
      <div className="mb-6 inline-flex items-center bg-gray-100 rounded-lg p-1 gap-1">
        {FILTERS.map((f) => {
          const active = mode === f.id;
          const { Icon } = f;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setMode(f.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                active
                  ? "bg-white text-[#002855] shadow-sm"
                  : "text-gray-600 hover:text-[#002855]"
              }`}
            >
              <Icon className="w-4 h-4" />
              {f.label}
            </button>
          );
        })}
      </div>

      {filteredSections.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
          <p className="text-gray-500">Keine Kapitel in diesem Modus.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredSections.map((section) => (
            <SectionCard
              key={section.id}
              section={section}
              chapters={section.filteredChapters}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HandbookOverview;
